import { useState, useEffect, useMemo, useLayoutEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Accordion, Card, Col, Container, Row, Table } from 'react-bootstrap';
import { selectProjectResults, setSelectedVersion } from '../project-details/data-access/project.reducer';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent, GraphicComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Typography } from '@mui/material';
import VersionHistory, { VersionItem } from '../../../components/common/VersionHistory';
import { BiasVersionHistoryStrip } from './BiasDetection';
import { isWorkbenchNonRerunRun } from '../../../utils/workbenchRunFilter';
import { deriveExplainabilityFromLegacy, EXPLAINABILITY_CONFIG } from './explainabilityEnterprise.utils';
import type { LegacyExplainabilityEntry } from './explainabilityEnterprise.utils';
import {
  getPlot1SourceAttributionOption,
  getPlot2ShapTopKOption,
  getPlot4FaithfulnessGaugeOption
} from './explainabilityEnterpriseCharts';
import type { LimeStabilityRow } from './explainabilityEnterprise.utils';
import { ExplainabilityModuleKpiGrid } from './ModuleKpiDashboard';
import './BiasDetectionChartsSection.css';
import './FairnessPlotsSection.css';

echarts.use([TooltipComponent, GridComponent, LegendComponent, GraphicComponent, BarChart, CanvasRenderer]);

/** Match `FairnessPlotsSection` / Bias LLM plot card headings (slate subtitle). */
const EXPLAINABILITY_PLOT_TITLE_SX = {
  fontWeight: 600,
  mb: 1,
  color: '#334155',
  fontSize: '0.9rem'
} as const;

/** Match `--phoenix-navbar-top-height` for sticky offset and scroll “stuck” detection. */
function readPhoenixNavbarTopPx(): number {
  if (typeof window === 'undefined') return 72;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--phoenix-navbar-top-height').trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 72;
}

export interface ExplainabilityRef {
  handleHistoryOpen: (lane?: 'ml' | 'llm') => void;
}

type ExplainabilityDataItem = {
  Feature: string;
  Importance: number;
  [key: string]: string | number | undefined;
};

/** Parse "FeatureName (0.42)" or "FeatureName (freq: 0.42)" style string into { Feature, Importance }. */
const parseTokenOrFeatureString = (s: string): ExplainabilityDataItem | null => {
  const trimmed = String(s).trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(.+)\s*\(([\d.eE+-]+)\)$/);
  if (match) {
    const feature = match[1].trim();
    const importance = parseFloat(match[2]);
    return { Feature: feature, Importance: Number.isFinite(importance) ? importance : 0 };
  }
  return { Feature: trimmed, Importance: 0 };
};

const parseExplainabilityEntries = (input: unknown): ExplainabilityDataItem[] => {
  if (!input) return [];
  if (Array.isArray(input)) {
    return input
      .map(entry => {
        if (typeof entry === 'string') {
          return parseTokenOrFeatureString(entry);
        }
        if (typeof entry === 'object' && entry !== null) {
          const e = entry as Record<string, unknown>;
          const feature = e.Feature ?? e.feature ?? e.Token ?? e.token ?? e.attribute ?? 'Feature';
          const importance =
            e.Importance ??
            e.importance ??
            e.weight ??
            e.score ??
            e.value ??
            (e as Record<string, unknown>)['SHAP Contribution'] ??
            (e as Record<string, unknown>)['LIME Score'] ??
            (e as Record<string, unknown>)['shap_contribution'] ??
            (e as Record<string, unknown>)['lime_score'] ??
            0;
          const num = Number(importance);
          return {
            Feature: String(feature),
            Importance: Number.isFinite(num) ? num : 0,
            ...(e as object)
          } as ExplainabilityDataItem;
        }
        return null;
      })
      .filter(Boolean) as ExplainabilityDataItem[];
  }
  if (typeof input === 'object' && input !== null) {
    const obj = input as Record<string, unknown>;

    // Handle objects with tokens + relative_influence (e.g., top_tokens or shap_topk)
    const tkns = obj.tokens ?? (obj.shap_topk as any)?.tokens;
    const influence = obj.relative_influence ?? (obj.shap_topk as any)?.relative_influence;
    if (Array.isArray(tkns) && Array.isArray(influence)) {
      const categories = obj.categories ?? (obj.shap_topk as any)?.categories;
      return tkns.map((t, i) => ({
        Feature: String(t),
        Importance: Number(influence[i] ?? 0),
        Category: Array.isArray(categories) ? String(categories[i] ?? '') : undefined
      })) as ExplainabilityDataItem[];
    }

    const nested = obj.top_features ?? obj.features ?? obj.tokens ?? obj.data ?? obj.entries;
    if (Array.isArray(nested)) return parseExplainabilityEntries(nested);

    // Support nested shap_topk structure (fallback lookup if not caught above)
    if (obj.shap_topk && typeof obj.shap_topk === 'object') {
      const shap = obj.shap_topk as Record<string, unknown>;
      const tokens = shap.tokens;
      const influence2 = shap.relative_influence;
      if (Array.isArray(tokens) && Array.isArray(influence2)) {
        return tokens.map((t, i) => ({
          Feature: String(t),
          Importance: Number(influence2[i] ?? 0),
          Category: Array.isArray(shap.categories) ? String(shap.categories[i] ?? '') : undefined
        })) as ExplainabilityDataItem[];
      }
    }
  }
  return [];
};

const renderChart = (
  option: echarts.EChartsCoreOption | null,
  height = 320,
  fallbackMessage = 'No explainability data available.'
) =>
  option ? (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      notMerge
      lazyUpdate
      style={{ width: '100%', height: `${height}px` }}
      opts={{ renderer: 'canvas' }}
    />
  ) : (
    <div className="d-flex align-items-center justify-content-center" style={{ width: '100%', height: `${height}px` }}>
      <p className="text-muted mb-0">{fallbackMessage}</p>
    </div>
  );

type ExplainabilityVersionItem = {
  result_id?: string;
  title?: string;
  timestamp?: string;
  userId?: string;
  [key: string]: unknown;
};

const explainabilityRunHasDataKeys = (node: Record<string, unknown>, keys: string[]): boolean => {
  const hasData = (o: unknown) =>
    o != null && typeof o === 'object' && (Array.isArray(o) ? o.length > 0 : Object.keys(o as object).length > 0);
  return keys.some(k => hasData(node[k]));
};

const ML_SCAN_KEYS = [
  'ML_Model_Explainability',
  'ml_model_explainability',
  'mlExplainability',
  'ml_explainability',
  'ML',
  'ml'
] as const;

const LLM_SCAN_KEYS = [
  'LLM_Model_Explainability',
  'llm_model_explainability',
  'llmExplainability',
  'llm_explainability',
  'LLM',
  'llm'
] as const;

function isExplainabilityRecord(u: unknown): u is Record<string, unknown> {
  return u != null && typeof u === 'object' && !Array.isArray(u);
}

/**
 * Unwrap `LLM_Model_Explainability` / `llm_model_explainability` when present on a node.
 * Arrays of blocks use the first object element.
 */
function unwrapLlmBranchFromContainer(o: Record<string, unknown>): Record<string, unknown> | null {
  const raw =
    o.LLM_Model_Explainability ?? o.llm_model_explainability ?? o.llmExplainability ?? o.llm_explainability ?? o.llm;
  if (Array.isArray(raw)) {
    for (const el of raw) {
      if (isExplainabilityRecord(el)) return el;
    }
    return null;
  }
  if (isExplainabilityRecord(raw)) return raw;
  return null;
}

/** True when the node itself is already the LLM explainability governance payload (no wrapper keys). */
function looksLikeLlmExplainabilityPayload(o: Record<string, unknown>): boolean {
  return (
    o.microeai_explainability != null ||
    o.Microeai_Explainability != null ||
    o.risk_score != null ||
    o.Risk_Score != null ||
    o.source_attribution != null ||
    o.shap_topk != null ||
    o.lime_stability != null ||
    o.faithfulness != null ||
    o.metrics != null
  );
}

/**
 * Resolves the LLM explainability block used for KPIs and charts. Matches API paths such as
 * `results.explainability[0].llm_model_explainability` as well as a flat run payload.
 */
function resolveLlmExplainabilityPayload(
  node: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!node) return null;

  const fromContainer = unwrapLlmBranchFromContainer(node);
  if (fromContainer) return fromContainer;
  if (looksLikeLlmExplainabilityPayload(node)) return node;

  const walkExplainabilitySlots = (container: unknown): Record<string, unknown> | null => {
    if (!isExplainabilityRecord(container)) return null;
    const expl = container.explainability;
    const slots: unknown[] = Array.isArray(expl) ? expl : expl != null ? [expl] : [];
    for (const slot of slots) {
      if (!isExplainabilityRecord(slot)) continue;
      const hit = resolveLlmExplainabilityPayload(slot);
      if (hit) return hit;
    }
    return null;
  };

  const nested =
    walkExplainabilitySlots(node.results) ??
    walkExplainabilitySlots(node.feature_results) ??
    walkExplainabilitySlots(node.collection_results) ??
    walkExplainabilitySlots(node);
  if (nested) return nested;

  return null;
}

/** Layers to scan when API nests payloads under `results` / `explainability` / etc. */
function explainabilityScanLayers(node: Record<string, unknown>): Record<string, unknown>[] {
  const layers: Record<string, unknown>[] = [node];
  const nest = [
    node.results,
    node.explainability,
    node.data,
    node.payload,
    node.feature_results,
    node.collection_results
  ];
  for (const v of nest) {
    if (v && typeof v === 'object' && !Array.isArray(v)) layers.push(v as Record<string, unknown>);
  }
  const pushExplainabilityArrayElements = (container: unknown) => {
    if (!isExplainabilityRecord(container)) return;
    const ex = container.explainability;
    if (Array.isArray(ex)) {
      ex.forEach(el => {
        if (isExplainabilityRecord(el)) layers.push(el);
      });
    }
  };
  pushExplainabilityArrayElements(node.results);
  pushExplainabilityArrayElements(node);
  pushExplainabilityArrayElements(node.feature_results);
  pushExplainabilityArrayElements(node.collection_results);

  const llmBranch =
    node.LLM_Model_Explainability ?? node.llm_model_explainability ?? node.llmExplainability ?? node.llm_explainability;
  if (llmBranch && typeof llmBranch === 'object' && !Array.isArray(llmBranch)) {
    layers.push(llmBranch as Record<string, unknown>);
  }
  const mlBranch =
    node.ML_Model_Explainability ?? node.ml_model_explainability ?? node.mlExplainability ?? node.ml_explainability;
  if (mlBranch && typeof mlBranch === 'object' && !Array.isArray(mlBranch)) {
    layers.push(mlBranch as Record<string, unknown>);
  }
  return layers;
}

function layerHasBranchKeys(layer: Record<string, unknown>, keys: readonly string[]): boolean {
  return keys.some(k => explainabilityRunHasDataKeys(layer, [k]));
}

/** True if any scanned layer carries ML explainability payloads (matches table extraction). */
function explainabilityVersionHasMlDeep(node: Record<string, unknown>): boolean {
  for (const layer of explainabilityScanLayers(node)) {
    if (layerHasBranchKeys(layer, ML_SCAN_KEYS)) return true;
  }
  return false;
}

/** True if any scanned layer carries LLM explainability payloads or typical backend LLM-only fields. */
function explainabilityVersionHasLlmDeep(node: Record<string, unknown>): boolean {
  for (const layer of explainabilityScanLayers(node)) {
    if (layerHasBranchKeys(layer, LLM_SCAN_KEYS)) return true;
    if (
      layer.microeai_explainability ||
      layer.source_attribution ||
      layer.shap_topk ||
      layer.lime_stability ||
      layer.faithfulness != null
    ) {
      return true;
    }
  }
  return false;
}

/** Count runs that include ML vs LLM payloads (for Workbench nav — mirrors nested extraction paths). */
const countExplainabilityMlLlmRuns = (versions: ExplainabilityVersionItem[]) => {
  let ml = 0;
  let llm = 0;
  for (const v of versions) {
    if (!v || typeof v !== 'object') continue;
    const n = v as Record<string, unknown>;
    if (explainabilityVersionHasMlDeep(n)) ml += 1;
    if (explainabilityVersionHasLlmDeep(n)) llm += 1;
  }
  // Combined / legacy blobs sometimes omit typed keys but still represent explainability runs — show both lanes like Bias.
  if (versions.length > 0 && ml === 0 && llm === 0) {
    return { ml: versions.length, llm: versions.length };
  }
  return { ml, llm };
};

/** API often returns separate array entries per lane (`ml_explainability` / `llm_explainability`). */
const inferExplainabilityLane = (v: ExplainabilityVersionItem): 'ml' | 'llm' | 'both' | null => {
  const n = v as Record<string, unknown>;
  const at = String(n.analysis_type ?? '').toLowerCase();
  if (at.includes('ml_explainability') || at === 'ml') return 'ml';
  if (at.includes('llm_explainability') || at === 'llm') return 'llm';
  const hasMl = explainabilityVersionHasMlDeep(n);
  const hasLlm = explainabilityVersionHasLlmDeep(n);
  if (hasMl && hasLlm) return 'both';
  if (hasMl) return 'ml';
  if (hasLlm) return 'llm';
  return null;
};

const filterExplainabilityVersionsByLane = (
  versions: ExplainabilityVersionItem[],
  lane: 'ml' | 'llm'
): ExplainabilityVersionItem[] =>
  versions.filter(v => {
    const kind = inferExplainabilityLane(v);
    return kind === lane || kind === 'both';
  });

const pickDefaultMlExplainabilityVersion = (versions: ExplainabilityVersionItem[]): ExplainabilityVersionItem => {
  const withMl = versions.find(v => explainabilityVersionHasMlDeep(v as Record<string, unknown>));
  if (withMl) return withMl;
  return versions[0];
};

const pickDefaultLlmExplainabilityVersion = (versions: ExplainabilityVersionItem[]): ExplainabilityVersionItem => {
  const withLlm = versions.find(v => explainabilityVersionHasLlmDeep(v as Record<string, unknown>));
  if (withLlm) return withLlm;
  return versions[0];
};

const collectExplainabilityCandidates = (projectResults: unknown): unknown[] => {
  const pr = projectResults as Record<string, unknown> | undefined;
  if (!pr) return [];
  const results = pr.results as Record<string, unknown> | undefined;
  const featureResults = pr.feature_results as Record<string, unknown> | undefined;
  const collectionResults = pr.collection_results as Record<string, unknown> | undefined;
  return [
    results?.explainability,
    featureResults?.explainability,
    collectionResults?.explainability,
    results?.explainability_ml,
    results?.explainability_llm,
    pr.explainability,
    featureResults?.explainability_llm,
    collectionResults?.explainability_llm
  ].filter(Boolean);
};

/** Merge the fullest explainability payload for a run from project results (API may list stubs in version arrays). */
const mergeExplainabilityRunFromProjectResults = (
  run: ExplainabilityVersionItem,
  projectResults: unknown
): ExplainabilityVersionItem => {
  const resultId = run.result_id;
  if (!resultId || !projectResults) return run;

  let merged: Record<string, unknown> = { ...run };

  const visit = (candidate: unknown) => {
    if (!candidate || typeof candidate !== 'object') return;
    if (Array.isArray(candidate)) {
      candidate.forEach(visit);
      return;
    }
    const item = candidate as Record<string, unknown>;
    if (item.result_id === resultId) {
      merged = { ...merged, ...item };
    }
  };

  collectExplainabilityCandidates(projectResults).forEach(visit);
  return merged as ExplainabilityVersionItem;
};

export interface ExplainabilityVersionInfo {
  total: number;
  runsWithMl: number;
  runsWithLlm: number;
}

interface ExplainabilityTablesProps {
  onVersionsReady?: (info: ExplainabilityVersionInfo) => void;
  openHistoryOnMount?: boolean;
  onOpenHistoryConsumed?: () => void;
  /** When false, hide the chip + View All row (Workbench shows versions in the tab bar). Default true. */
  showInlineVersionControls?: boolean;
}

const ExplainabilityTables = forwardRef<ExplainabilityRef, ExplainabilityTablesProps>(
  ({ onVersionsReady, openHistoryOnMount, onOpenHistoryConsumed, showInlineVersionControls = true }, ref) => {
    const dispatch = useDispatch();
    const projectResults = useSelector(selectProjectResults);

    const [mlExplainabilityData, setMlExplainabilityData] = useState<ExplainabilityDataItem[]>([]);
    const [llmExplainabilityData, setLlmExplainabilityData] = useState<ExplainabilityDataItem[]>([]);
    const [mlHeaders, setMlHeaders] = useState<string[]>([]);
    const [llmHeaders, setLlmHeaders] = useState<string[]>([]);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyLaneFilter, setHistoryLaneFilter] = useState<'ml' | 'llm' | null>(null);
    const [explainabilityVersions, setExplainabilityVersions] = useState<ExplainabilityVersionItem[]>([]);
    const [activeMlRunId, setActiveMlRunId] = useState<string | null>(null);
    const [activeLlmRunId, setActiveLlmRunId] = useState<string | null>(null);
    const [isHeaderSticky, setIsHeaderSticky] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const stickyTopPxRef = useRef(readPhoenixNavbarTopPx());
    const [overviewAccordionKey, setOverviewAccordionKey] = useState<string | null>('llm-overview');
    const [tablesAccordionKey, setTablesAccordionKey] = useState<string | null>('tables');

    useImperativeHandle(ref, () => ({
      handleHistoryOpen: (lane?: 'ml' | 'llm') => {
        setHistoryLaneFilter(lane ?? null);
        setHistoryOpen(true);
      }
    }));

    useEffect(() => {
      if (openHistoryOnMount) {
        setHistoryLaneFilter(null);
        setHistoryOpen(true);
        onOpenHistoryConsumed?.();
      }
    }, [openHistoryOnMount, onOpenHistoryConsumed]);

    useEffect(() => {
      const versions: ExplainabilityVersionItem[] = [];
      const seen = new Set<string>();

      const push = (candidate: unknown) => {
        if (!candidate || typeof candidate !== 'object') return;
        if (Array.isArray(candidate)) {
          candidate.forEach(item => {
            if (item && typeof item === 'object' && isWorkbenchNonRerunRun(item)) {
              const v = item as ExplainabilityVersionItem;
              const id = v.result_id || (v.title && `${v.title}-${String(v.timestamp ?? '')}`) || `expl-${seen.size}`;
              if (id && !seen.has(id)) {
                seen.add(id);
                versions.push({ ...v, result_id: v.result_id || id });
              }
            }
          });
        } else {
          const v = candidate as ExplainabilityVersionItem;
          if (!isWorkbenchNonRerunRun(v)) return;
          const id = v.result_id || (v.title && `${v.title}-${String(v.timestamp ?? '')}`) || `expl-${seen.size}`;
          if (id && !seen.has(id)) {
            seen.add(id);
            versions.push({ ...v, result_id: v.result_id || id });
          }
        }
      };

      push(projectResults?.results?.explainability);
      push(projectResults?.feature_results?.explainability);
      push(projectResults?.collection_results?.explainability);
      push(projectResults?.results?.explainability_ml);
      push(projectResults?.results?.explainability_llm);
      push((projectResults as Record<string, unknown> | undefined)?.explainability);
      push(projectResults?.feature_results?.explainability_llm);
      push(projectResults?.collection_results?.explainability_llm);
      setExplainabilityVersions(versions);
      const { ml, llm } = countExplainabilityMlLlmRuns(versions);
      onVersionsReady?.({ total: versions.length, runsWithMl: ml, runsWithLlm: llm });
    }, [projectResults, onVersionsReady]);

    useEffect(() => {
      if (explainabilityVersions.length === 0) {
        setActiveMlRunId(null);
        setActiveLlmRunId(null);
        return;
      }
      const mlVersions = filterExplainabilityVersionsByLane(explainabilityVersions, 'ml');
      const llmVersions = filterExplainabilityVersionsByLane(explainabilityVersions, 'llm');

      const hasMlActive = activeMlRunId && mlVersions.some(v => v.result_id === activeMlRunId);
      if (!hasMlActive && mlVersions.length > 0) {
        setActiveMlRunId(pickDefaultMlExplainabilityVersion(mlVersions).result_id ?? null);
      }

      const hasLlmActive = activeLlmRunId && llmVersions.some(v => v.result_id === activeLlmRunId);
      if (!hasLlmActive && llmVersions.length > 0) {
        setActiveLlmRunId(pickDefaultLlmExplainabilityVersion(llmVersions).result_id ?? null);
      }
    }, [explainabilityVersions, activeMlRunId, activeLlmRunId]);

    useLayoutEffect(() => {
      const syncTop = () => {
        stickyTopPxRef.current = readPhoenixNavbarTopPx();
      };
      syncTop();
      window.addEventListener('resize', syncTop);
      return () => window.removeEventListener('resize', syncTop);
    }, []);

    useEffect(() => {
      const handleScroll = () => {
        if (headerRef.current) {
          const rect = headerRef.current.getBoundingClientRect();
          setIsHeaderSticky(rect.top <= stickyTopPxRef.current + 0.5);
        }
      };

      handleScroll();
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const activeMlExplainabilityRun = useMemo(() => {
      const mlVersions = filterExplainabilityVersionsByLane(explainabilityVersions, 'ml');
      if (mlVersions.length === 0) return null;
      const picked = activeMlRunId ? mlVersions.find(v => v.result_id === activeMlRunId) : undefined;
      const run = picked ?? pickDefaultMlExplainabilityVersion(mlVersions);
      return mergeExplainabilityRunFromProjectResults(run, projectResults);
    }, [explainabilityVersions, activeMlRunId, projectResults]);

    const activeLlmExplainabilityRun = useMemo(() => {
      const llmVersions = filterExplainabilityVersionsByLane(explainabilityVersions, 'llm');
      if (llmVersions.length === 0) return null;
      const picked = activeLlmRunId ? llmVersions.find(v => v.result_id === activeLlmRunId) : undefined;
      const run = picked ?? pickDefaultLlmExplainabilityVersion(llmVersions);
      return mergeExplainabilityRunFromProjectResults(run, projectResults);
    }, [explainabilityVersions, activeLlmRunId, projectResults]);

    const explainabilitySources = useMemo(() => {
      const sources: unknown[] = [];
      if (activeMlExplainabilityRun) sources.push(activeMlExplainabilityRun);
      if (activeLlmExplainabilityRun) sources.push(activeLlmExplainabilityRun);
      if (sources.length > 0) return sources;

      const fallback: unknown[] = [];
      const seen = new Set<string>();

      const push = (candidate: unknown) => {
        if (!candidate || typeof candidate !== 'object') return;
        if (Array.isArray(candidate)) {
          candidate.forEach(item => {
            if (item && typeof item === 'object' && isWorkbenchNonRerunRun(item)) {
              const v = item as ExplainabilityVersionItem;
              const id = v.result_id || (v.title && `${v.title}-${String(v.timestamp ?? '')}`) || `expl-${seen.size}`;
              if (!seen.has(id)) seen.add(id);
              fallback.push(item);
            }
          });
        } else {
          const v = candidate as ExplainabilityVersionItem;
          if (!isWorkbenchNonRerunRun(v)) return;
          const id = v.result_id || (v.title && `${v.title}-${String(v.timestamp ?? '')}`) || `expl-${seen.size}`;
          if (!seen.has(id)) seen.add(id);
          fallback.push(candidate);
        }
      };

      push(projectResults?.results?.explainability);
      push(projectResults?.feature_results?.explainability);
      push(projectResults?.collection_results?.explainability);
      push(projectResults?.results?.explainability_ml);
      push(projectResults?.results?.explainability_llm);
      push((projectResults as Record<string, unknown> | undefined)?.explainability);
      push(projectResults?.feature_results?.explainability_llm);
      push(projectResults?.collection_results?.explainability_llm);
      return fallback;
    }, [projectResults, activeMlExplainabilityRun, activeLlmExplainabilityRun]);

    useEffect(() => {
      const mlEntries: ExplainabilityDataItem[] = [];
      const llmEntries: ExplainabilityDataItem[] = [];
      const isRecord = (v: unknown): v is Record<string, unknown> => Boolean(v) && typeof v === 'object';

      const extract = (node: unknown) => {
        if (!node) return;
        if (Array.isArray(node)) {
          node.forEach(extract);
          return;
        }
        if (!isRecord(node)) return;
        const hasData = (o: unknown) =>
          o && typeof o === 'object' && (Array.isArray(o) ? o.length > 0 : Object.keys(o).length > 0);

        const mlCandidates = [
          node.ML_Model_Explainability,
          node.ml_model_explainability,
          node.mlExplainability,
          node.ml_explainability,
          node.ML,
          node.ml
        ].filter(hasData);
        const llmCandidates = [
          node.LLM_Model_Explainability,
          node.llm_model_explainability,
          node.llmExplainability,
          node.llm_explainability,
          node.LLM,
          node.llm
        ].filter(hasData);

        const pushFromItem = (item: Record<string, unknown>, into: ExplainabilityDataItem[]) => {
          const top = (item.top_features ?? item.top_tokens) as unknown;
          if (top) parseExplainabilityEntries(top).forEach(e => into.push(e));
          const shapContrib = (item.Sensitive_Feature_Contributions ?? item.sensitive_feature_contributions) as unknown;
          if (Array.isArray(shapContrib)) parseExplainabilityEntries(shapContrib).forEach(e => into.push(e));
          const limeInsights = (item.LIME_Feature_Insights ?? item.lime_feature_insights) as unknown;
          if (Array.isArray(limeInsights)) parseExplainabilityEntries(limeInsights).forEach(e => into.push(e));
          if (into.length === 0) parseExplainabilityEntries(item).forEach(e => into.push(e));
        };

        mlCandidates.forEach(c => {
          if (Array.isArray(c)) {
            (c as unknown[]).forEach((item: unknown) => {
              if (isRecord(item)) pushFromItem(item, mlEntries);
            });
          } else if (isRecord(c)) {
            pushFromItem(c, mlEntries);
          }
        });
        llmCandidates.forEach(c => {
          if (Array.isArray(c)) {
            (c as unknown[]).forEach((item: unknown) => {
              if (isRecord(item)) pushFromItem(item, llmEntries);
            });
          } else if (isRecord(c)) {
            pushFromItem(c, llmEntries);
          }
        });

        if (Array.isArray(node.entries)) node.entries.forEach(extract);
        if (Array.isArray(node.data)) node.data.forEach(extract);
        if (isRecord(node.results) && node.results.explainability) extract(node.results.explainability);
        if (isRecord(node.explainability)) extract(node.explainability);
      };

      explainabilitySources.forEach(extract);
      setMlExplainabilityData(mlEntries);
      setLlmExplainabilityData(llmEntries);
      setMlHeaders(mlEntries.length ? Object.keys(mlEntries[0]) : ['Feature', 'Importance']);
      setLlmHeaders(llmEntries.length ? Object.keys(llmEntries[0]) : ['Feature', 'Importance']);
    }, [explainabilitySources]);

    const derived = useMemo(() => {
      const ml: LegacyExplainabilityEntry[] = mlExplainabilityData.map(e => ({
        Feature: e.Feature,
        Importance: e.Importance
      }));
      const llm: LegacyExplainabilityEntry[] = llmExplainabilityData.map(e => ({
        Feature: e.Feature,
        Importance: e.Importance
      }));
      const d = deriveExplainabilityFromLegacy(ml, llm);

      // Overwrite with backend metrics if available (Python explainability_ui_data.json or LLM_Model_Explainability)
      let backendMetrics: any = null;
      for (const src of explainabilitySources) {
        if (src && typeof src === 'object') {
          const node = src as Record<string, unknown>;
          const llmExpl = resolveLlmExplainabilityPayload(node);
          if (llmExpl && typeof llmExpl === 'object') {
            const b = llmExpl as Record<string, any>;
            const hasBackendData =
              b.microeai_explainability ||
              b.source_attribution ||
              b.shap_topk?.tokens?.length ||
              b.lime_stability?.rows?.length ||
              b.faithfulness?.mean_aopc != null ||
              b.median_lime_std !== undefined;
            if (hasBackendData) {
              backendMetrics = llmExpl;
              break;
            }
          }
        }
      }

      if (backendMetrics) {
        // microeai_explainability (XRS score, verdict, drivers)
        if (backendMetrics.microeai_explainability) {
          d.xrs = backendMetrics.microeai_explainability.score ?? d.xrs;
          d.verdict = backendMetrics.microeai_explainability.verdict ?? d.verdict;
          d.drivers = { ...d.drivers, ...(backendMetrics.microeai_explainability.drivers || {}) };
        }
        // median_lime_std from lime_stability or top-level
        d.medianLimeStd =
          backendMetrics.lime_stability?.median_std ?? backendMetrics.median_lime_std ?? d.medianLimeStd;
        // mean_aopc from faithfulness or top-level (Python backend uses faithfulness.mean_aopc)
        d.meanAopc = backendMetrics.faithfulness?.mean_aopc ?? backendMetrics.mean_aopc ?? d.meanAopc;
        if (backendMetrics.developer_guidance) {
          d.developerGuidance = backendMetrics.developer_guidance;
        }
        // lime_stability.rows (Plot 3 — LIME scorecard)
        if (backendMetrics.lime_stability?.rows?.length) {
          d.limeStability = backendMetrics.lime_stability.rows.map((r: any) => ({
            token: r.token ?? '',
            status: r.status ?? 'Stable',
            std: Number(r.std) || 0
          }));
        }
        // source_attribution (Plot 1 — Where does influence come from?)
        if (backendMetrics.source_attribution && Object.keys(backendMetrics.source_attribution).length > 0) {
          d.sourceAttribution = backendMetrics.source_attribution;
        }
        // shap_topk (Plot 2 — SHAP-style Top-K)
        if (backendMetrics.shap_topk?.tokens?.length) {
          const st = backendMetrics.shap_topk;
          const tokens = Array.isArray(st.tokens) ? st.tokens : [];
          const rel = Array.isArray(st.relative_influence) ? st.relative_influence : [];
          const cats = Array.isArray(st.categories) ? st.categories : [];
          d.shapTopK = tokens.map((t: string, i: number) => ({
            token: String(t ?? ''),
            relativeInfluence: Number(rel[i]) ?? 0,
            category: (cats[i] === 'Sensitive' || cats[i] === 'Whitelisted' ? cats[i] : 'Neutral') as
              | 'Sensitive'
              | 'Whitelisted'
              | 'Neutral'
          }));
        }
      }

      return d;
    }, [mlExplainabilityData, llmExplainabilityData, explainabilitySources]);

    // Backend LLM explainability has source_attribution, shap_topk, etc. even when arrays are empty
    const hasBackendLLMExplainability = useMemo(() => {
      for (const src of explainabilitySources) {
        if (src && typeof src === 'object') {
          const llmExpl = resolveLlmExplainabilityPayload(src as Record<string, unknown>);
          if (llmExpl && typeof llmExpl === 'object') {
            if (
              llmExpl.microeai_explainability ||
              (llmExpl.source_attribution && Object.keys(llmExpl.source_attribution as object).length > 0) ||
              llmExpl.shap_topk ||
              llmExpl.lime_stability ||
              llmExpl.faithfulness ||
              llmExpl.median_lime_std !== undefined ||
              llmExpl.risk_score != null ||
              llmExpl.Risk_Score != null
            ) {
              return true;
            }
          }
        }
      }
      return false;
    }, [explainabilitySources]);

    const llmExplainabilityKpiSource = useMemo((): Record<string, unknown> | null => {
      const run = activeLlmExplainabilityRun as Record<string, unknown> | undefined;
      if (!run) return null;
      return resolveLlmExplainabilityPayload(run);
    }, [activeLlmExplainabilityRun]);

    const plot1 = useMemo(
      () => getPlot1SourceAttributionOption(derived.sourceAttribution),
      [derived.sourceAttribution]
    );
    const plot2 = useMemo(() => getPlot2ShapTopKOption(derived.shapTopK), [derived.shapTopK]);
    const plot4 = useMemo(() => getPlot4FaithfulnessGaugeOption(derived.meanAopc), [derived.meanAopc]);

    const ruleText = `Stable ≤ ${EXPLAINABILITY_CONFIG.lime_stable_mult}×median_std; Moderate ≤ ${EXPLAINABILITY_CONFIG.lime_moderate_mult}×median_std; else Unstable`;

    const renderTableRows = (data: ExplainabilityDataItem[], headers: string[]) =>
      data.map((row, i) => (
        <tr key={i}>
          {headers.map((h, j) => (
            <td key={j}>{typeof row[h] === 'number' ? (row[h] as number).toFixed(4) : String(row[h] ?? '-')}</td>
          ))}
        </tr>
      ));

    const formatTimestamp = (v: ExplainabilityVersionItem): string => {
      const ts = v.timestamp;
      if (typeof ts === 'object' && ts !== null && (ts as { $date?: number }).$date != null) {
        const ms = (ts as { $date: number }).$date;
        return typeof ms === 'number' ? new Date(ms).toISOString() : String(ms);
      }
      return typeof ts === 'string' ? ts : String(ts ?? '');
    };

    const versionsForHistory = useMemo(() => {
      if (!historyLaneFilter) return explainabilityVersions;
      return filterExplainabilityVersionsByLane(explainabilityVersions, historyLaneFilter);
    }, [explainabilityVersions, historyLaneFilter]);

    const versionItems: VersionItem[] = useMemo(
      () =>
        versionsForHistory.map((version, index) => ({
          id: version.result_id ?? `expl-${index}`,
          timestamp: formatTimestamp(version),
          date: formatTimestamp(version),
          modifier: (version.userId as string) || 'System',
          isCurrent:
            historyLaneFilter === 'ml'
              ? activeMlRunId === version.result_id
              : historyLaneFilter === 'llm'
              ? activeLlmRunId === version.result_id
              : activeMlRunId === version.result_id || activeLlmRunId === version.result_id,
          title: (version.title as string) || `Explainability ${index + 1}`
        })),
      [versionsForHistory, activeMlRunId, activeLlmRunId, historyLaneFilter]
    );

    const historyDrawerTitle =
      historyLaneFilter === 'ml'
        ? 'Explainability — ML versions'
        : historyLaneFilter === 'llm'
        ? 'Explainability — LLM versions'
        : 'Explainability — All versions';

    const handleVersionHistorySelect = (item: VersionItem) => {
      const raw = explainabilityVersions.find(v => (v.result_id ?? v.title) === item.id || v.result_id === item.id);
      if (raw) {
        const lane = historyLaneFilter ?? inferExplainabilityLane(raw);
        if (lane === 'ml' || lane === 'both') setActiveMlRunId(raw.result_id ?? null);
        if (lane === 'llm' || lane === 'both') setActiveLlmRunId(raw.result_id ?? null);
        dispatch(setSelectedVersion(raw));
      }
      setHistoryOpen(false);
      setHistoryLaneFilter(null);
    };

    const versionSummary = countExplainabilityMlLlmRuns(explainabilityVersions);

    return (
      <div>
        {showInlineVersionControls ? (
          <div
            ref={headerRef}
            style={{
              position: 'sticky',
              top: 'var(--phoenix-navbar-top-height, 72px)',
              zIndex: 1100,
              backgroundColor: isHeaderSticky ? 'var(--bs-body-bg)' : 'transparent',
              borderBottom: isHeaderSticky ? '1px solid var(--bs-border-color-translucent)' : 'none',
              backdropFilter: isHeaderSticky ? 'blur(8px)' : 'none',
              boxShadow: isHeaderSticky ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
              marginBottom: '4px',
              transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
              width: '100%',
              left: 0,
              right: 0
            }}
          >
            <Container fluid className="px-0 px-sm-1 px-lg-2">
              <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 py-0 min-h-0">
                <BiasVersionHistoryStrip
                  llmCount={versionSummary.llm}
                  mlCount={versionSummary.ml}
                  onOpenLlm={() => {
                    setHistoryLaneFilter('llm');
                    setHistoryOpen(true);
                  }}
                  onOpenMl={() => {
                    setHistoryLaneFilter('ml');
                    setHistoryOpen(true);
                  }}
                />
              </div>
            </Container>
          </div>
        ) : (
          <div ref={headerRef} className="d-none" aria-hidden />
        )}

        <Container fluid className="px-0 px-sm-1 px-lg-2">
          {explainabilityVersions.length > 0 ? (
            <>
              <ExplainabilityModuleKpiGrid llm={llmExplainabilityKpiSource} />

              {(llmExplainabilityData.length > 0 || hasBackendLLMExplainability) && (
                <Accordion
                  activeKey={overviewAccordionKey}
                  onSelect={k => setOverviewAccordionKey(k === 'llm-overview' ? 'llm-overview' : null)}
                  className="bias-detection-accordion border-0 rounded-4 shadow-sm mb-2 overflow-hidden"
                >
                  <Accordion.Item eventKey="llm-overview" className="border-0 bg-white">
                    <Accordion.Header className="bias-detection-accordion__header px-2 py-1 fw-semibold">
                      LLM explainability analysis
                    </Accordion.Header>
                    <Accordion.Body className="p-2">
                      {activeLlmExplainabilityRun?.title ? (
                        <Typography variant="subtitle2" color="text.secondary" className="mb-2">
                          {String(activeLlmExplainabilityRun.title)}
                        </Typography>
                      ) : null}
                      {/* <Card className="border-0 shadow-sm rounded-4 mb-2 overflow-hidden bg-body-secondary bg-opacity-10">
                        <Card.Body className="p-2 p-md-3">
                          <Typography variant="subtitle1" className="fw-semibold mb-2">
                            Explainability aggregate summary
                          </Typography>
                          <Row className="g-2">
                            <Col md={6}>
                              <p className="mb-1 text-muted small">Score (XRS)</p>
                              <p className="fw-semibold mb-2">{derived.xrs.toFixed(3)}</p>
                              <p className="mb-1 text-muted small">Verdict</p>
                              <p className="fw-semibold mb-2">{derived.verdict}</p>
                              <p className="mb-1 text-muted small">Thresholds</p>
                              <p className="small mb-0">
                                LOW ≤ {EXPLAINABILITY_CONFIG.xrs_low}, MODERATE ≤ {EXPLAINABILITY_CONFIG.xrs_high}, HIGH
                                &gt; {EXPLAINABILITY_CONFIG.xrs_high}
                              </p>
                            </Col>
                            <Col md={6}>
                              <p className="mb-1 text-muted small">Risk drivers</p>
                              <ul className="small mb-2 list-unstyled">
                                {Object.entries(derived.drivers).map(([k, v]) => (
                                  <li key={k}>
                                    {k}: {typeof v === 'number' ? v.toFixed(4) : String(v)}
                                  </li>
                                ))}
                              </ul>
                              {derived.developerGuidance.length > 0 && (
                                <>
                                  <p className="mb-1 text-muted small">Developer guidance</p>
                                  <ul className="small mb-0">
                                    {derived.developerGuidance.map((g, i) => (
                                      <li key={i}>{g}</li>
                                    ))}
                                  </ul>
                                </>
                              )}
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card> */}

                      <div className="fairness-plots-compact">
                        <Row className="g-2 mb-2">
                          <Col xs={12} lg={6}>
                            <Card className="shadow-sm border-0 rounded-4 overflow-visible h-100 bg-white">
                              <Card.Body className="p-2">
                                <Typography variant="subtitle2" sx={EXPLAINABILITY_PLOT_TITLE_SX}>
                                  Plot 1 — Where does influence come from?
                                </Typography>
                                {renderChart(plot1, 320, 'No source attribution data.')}
                                <p
                                  className="mb-0 pt-1"
                                  style={{ fontSize: 10, color: '#64748b', marginTop: 4, paddingLeft: 2 }}
                                >
                                  How to read: prompt-dominated → prompt design risk; metadata-dominated → configuration
                                  governance risk; output-dominated → model behavior risk.
                                </p>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col xs={12} lg={6}>
                            <Card className="shadow-sm border-0 rounded-4 overflow-visible h-100 bg-white">
                              <Card.Body className="p-2">
                                <Typography variant="subtitle2" sx={EXPLAINABILITY_PLOT_TITLE_SX}>
                                  Plot 2 — SHAP-style: Which tokens influenced the system most?
                                </Typography>
                                {renderChart(plot2, 320, 'No SHAP Top-K data.')}
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>

                        <Card className="shadow-sm border-0 rounded-4 mb-2 overflow-visible bg-white">
                          <Card.Body className="p-2">
                            <Typography variant="subtitle2" sx={EXPLAINABILITY_PLOT_TITLE_SX}>
                              Plot 3 — LIME-style Stability
                            </Typography>
                            <p className="small text-muted mb-2">{ruleText}</p>
                            <div className="table-responsive">
                              <Table striped bordered hover size="sm" className="mb-0">
                                <thead>
                                  <tr>
                                    <th>Token (Top-K)</th>
                                    <th>Stability</th>
                                    <th>Std Dev</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {derived.limeStability.length === 0 ? (
                                    <tr>
                                      <td colSpan={3} className="text-muted">
                                        No stability data
                                      </td>
                                    </tr>
                                  ) : (
                                    derived.limeStability.map((row: LimeStabilityRow, i: number) => (
                                      <tr key={i}>
                                        <td>{row.token}</td>
                                        <td>{row.status}</td>
                                        <td>{row.std.toFixed(4)}</td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </Table>
                            </div>
                          </Card.Body>
                        </Card>

                        <Card className="shadow-sm border-0 rounded-4 overflow-visible bg-white">
                          <Card.Body className="p-2">
                            <Typography variant="subtitle2" sx={EXPLAINABILITY_PLOT_TITLE_SX}>
                              Plot 4 — Do explanations really matter? (Faithfulness)
                            </Typography>
                            {renderChart(plot4, 220, 'No faithfulness data.')}
                          </Card.Body>
                        </Card>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              )}

              <Accordion
                activeKey={tablesAccordionKey}
                onSelect={k => setTablesAccordionKey(k === 'tables' ? 'tables' : null)}
                className="bias-detection-accordion border-0 rounded-4 shadow-sm overflow-hidden"
              >
                <Accordion.Item eventKey="tables" className="border-0 bg-white">
                  <Accordion.Header className="bias-detection-accordion__header px-2 py-1 fw-semibold">
                    Explainability - Request Data Analysis
                  </Accordion.Header>
                  <Accordion.Body className="p-2">
                    {mlExplainabilityData.length > 0 && (
                      <Card className="border-0 shadow-sm rounded-4 mb-2 overflow-hidden bg-white">
                        <Card.Body className="p-2 p-md-3">
                          <Typography variant="subtitle1" component="h3" className="fw-semibold mb-2">
                            ML explainability - Request Data Analysis
                          </Typography>
                          <div className="table-responsive">
                            <Table striped bordered hover className="table-sm align-middle mb-0">
                              <thead>
                                <tr>
                                  {mlHeaders.map((h, i) => (
                                    <th key={i}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>{renderTableRows(mlExplainabilityData, mlHeaders)}</tbody>
                            </Table>
                          </div>
                        </Card.Body>
                      </Card>
                    )}

                    {(llmExplainabilityData.length > 0 || hasBackendLLMExplainability) && (
                      <Card className="border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                        <Card.Body className="p-2 p-md-3">
                          <Typography variant="subtitle1" component="h3" className="fw-semibold mb-2">
                            LLM explainability - Request Data Analysis
                          </Typography>
                          <div className="table-responsive">
                            <Table striped bordered hover className="table-sm align-middle mb-0">
                              <thead>
                                <tr>
                                  {llmHeaders.map((h, i) => (
                                    <th key={i}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {llmExplainabilityData.length > 0 ? (
                                  renderTableRows(llmExplainabilityData, llmHeaders)
                                ) : (
                                  <tr>
                                    <td colSpan={llmHeaders.length} className="text-muted">
                                      No LLM explainability request rows for this run.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </div>
                        </Card.Body>
                      </Card>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </>
          ) : (
            <div className="d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
              <p className="text-muted">No explainability analysis data available. Run analysis to populate.</p>
            </div>
          )}
        </Container>

        <VersionHistory
          open={historyOpen}
          onClose={() => {
            setHistoryOpen(false);
            setHistoryLaneFilter(null);
          }}
          versions={versionItems}
          onVersionSelect={handleVersionHistorySelect}
          title={historyDrawerTitle}
        />
      </div>
    );
  }
);

ExplainabilityTables.displayName = 'ExplainabilityTables';

export default ExplainabilityTables;
